import { ErrorCode } from '@mogul/api-client';
import { parseApiErrors } from '@mogul/shared';
import React from 'react';

import { isValidPassword } from '../../../utils/profile-check';
import { Button, Colors } from '../../legacy/button/Button.component';
import { Panel } from '../panel/Panel.component';
import styles from './PasswordChangeModal.module.css';
interface Props {
  onSubmit: (input: {
    oldPassword: string;
    newPassword: string;
  }) => Promise<void>;
  hideModal: () => void;
}

interface PasswordChangeModalProps {
  hideModal: () => void;
}

interface PasswordErrors {
  currentPasswordError: string;
  newPasswordError: string;
  confirmPasswordError: string;
}

const PasswordChange: React.FC<Props> = ({ hideModal, onSubmit }: Props) => {
  const defaultErrors = {
    currentPasswordError: '',
    newPasswordError: '',
    confirmPasswordError: '',
  };
  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmNewPassword, setConfirmNewPassword] = React.useState('');
  const [errors, setErrors] = React.useState<PasswordErrors>(defaultErrors);

  const currentPasswordInput = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setOldPassword(e.target.value);
  };
  const newPasswordInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setNewPassword(e.target.value);
  };
  const confirmNewPasswordInput = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setConfirmNewPassword(e.target.value);
  };

  const validate = () => {
    const passwordErrors = defaultErrors;
    if (!isValidPassword(newPassword)) {
      passwordErrors.newPasswordError =
        'password must have at least 1 special character, 1 letter, 1 number, and must have a length of 8';
    }
    if (newPassword !== confirmNewPassword) {
      passwordErrors.confirmPasswordError = 'passwords do not match';
    }
    if (
      passwordErrors.newPasswordError !== '' ||
      passwordErrors.confirmPasswordError !== ''
    ) {
      setErrors(passwordErrors);
      return false;
    }
    setErrors(defaultErrors);
    return true;
  };

  return (
    <Panel
      className={styles.passwordChangeModal}
      bodyClassName={styles.passwordChangeModalBody}
      title="change password"
      hasExitModalButton
      hideModal={hideModal}
    >
      <p className={styles.passwordChangeText}>Current password</p>
      <input
        type="password"
        className={styles.passwordChangeInputs}
        value={oldPassword}
        onChange={currentPasswordInput}
      />
      <p className={styles.error}>{errors.currentPasswordError}</p>
      <p className={styles.passwordChangeText}>New password</p>
      <input
        type="password"
        className={styles.passwordChangeInputs}
        value={newPassword}
        onChange={newPasswordInput}
      />
      <p className={styles.error}>{errors.newPasswordError}</p>
      <p className={styles.passwordChangeText}>Confirm new password</p>
      <input
        type="password"
        className={styles.passwordChangeInputs}
        value={confirmNewPassword}
        onChange={confirmNewPasswordInput}
      />
      <p className={styles.error}>{errors.confirmPasswordError}</p>
      <Button
        color={Colors.COLOR_SCHEME_2}
        className={styles.passwordChangeBtn}
        onClick={async () => {
          const canChangePassword = validate();

          if (!canChangePassword) {
            return;
          }

          try {
            await onSubmit({
              oldPassword,
              newPassword,
            });
          } catch (error) {
            for (const { code } of parseApiErrors(error)) {
              if (code === ErrorCode.AuthUnauthorized) {
                setErrors({
                  ...errors,
                  currentPasswordError: 'Does not match current password',
                });
                return;
              }
            }

            throw error;
          }

          hideModal();
        }}
      >
        SAVE
      </Button>
    </Panel>
  );
};

export const PasswordChangeModal =
  (
    onSubmit: (input: {
      oldPassword: string;
      newPassword: string;
    }) => Promise<void>,
  ) =>
  ({ hideModal }: PasswordChangeModalProps) => {
    return <PasswordChange hideModal={hideModal} onSubmit={onSubmit} />;
  };
